/**
 * Der Warenkorb des Katalogs.
 *
 * Er liegt im BROWSER (localStorage) und nicht auf dem Server. Ein Warenkorb
 * ist ein Gedanke im Vorbeigehen: Man legt etwas hinein, sieht weiter, legt
 * noch etwas dazu. Ihn serverseitig zu fuehren hiesse, jede dieser Regungen
 * zu speichern – und dann die Frage zu beantworten, wann sie wieder
 * verschwindet. Verbindlich wird erst das Absenden; ab da liegt die Anfrage
 * in der Datenbank und ist ein Vorgang mit Nummer.
 *
 * Der Preis dafuer ist ehrlich zu nennen: Der Warenkorb folgt nicht auf ein
 * anderes Geraet, und wer die Browserdaten loescht, loescht ihn mit.
 *
 * Die Anzeigedaten (Name, Hersteller) liegen mit im Korb, damit die Seite
 * ohne zweite Abfrage auskommt. Verbindlich sind sie nicht: Was bestellt
 * wird, liest der Server beim Absenden frisch aus dem Katalog – sonst
 * koennte man sich einen Preis in den Korb schreiben.
 */

const STORAGE_KEY = 'audit.catalog.cart.v1';

/** Aenderungen im selben Tab; der Zaehler in der Kopfzeile haengt daran. */
const CHANGE_EVENT = 'audit:cart-changed';

export interface CartLine {
  catalogItemId: string;
  /** Nur fuer die Anzeige im Korb – verbindlich ist der Katalog. */
  name: string;
  manufacturer: string;
  quantity: number;
  note?: string;
  /** Zusatzangabe, wenn der Katalogeintrag eine verlangt (z. B. Gerätemodell). */
  orderFieldValue?: string;
}

export const MAX_CART_LINES = 25;
export const MAX_LINE_QUANTITY = 99;

/** Liest den Korb. Unlesbares wird verworfen, nicht geraten. */
export function readCart(storage: Storage | undefined = safeStorage()): CartLine[] {
  if (!storage) return [];
  try {
    const roh = storage.getItem(STORAGE_KEY);
    if (!roh) return [];
    const geparst: unknown = JSON.parse(roh);
    if (!Array.isArray(geparst)) return [];
    return geparst.filter(isCartLine).map(normalizeLine).slice(0, MAX_CART_LINES);
  } catch {
    // Beschaedigter Inhalt, gesperrter Speicher, privates Fenster: In all
    // diesen Faellen ist ein leerer Korb die richtige Antwort.
    return [];
  }
}

export function writeCart(lines: readonly CartLine[], storage: Storage | undefined = safeStorage()): void {
  try {
    storage?.setItem(STORAGE_KEY, JSON.stringify(lines.slice(0, MAX_CART_LINES)));
  } catch {
    // Kein Speicher verfuegbar: Der Korb lebt dann nur bis zum Neuladen.
  }
  if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent(CHANGE_EVENT));
}

/** Legt etwas hinein; dasselbe zweimal ist eine Menge, keine zweite Zeile. */
export function addLine(cart: readonly CartLine[], line: CartLine): CartLine[] {
  const vorhanden = cart.find((l) => l.catalogItemId === line.catalogItemId);
  if (vorhanden) {
    return cart.map((l) =>
      l.catalogItemId === line.catalogItemId ? { ...l, quantity: clampQuantity(l.quantity + line.quantity) } : l,
    );
  }
  if (cart.length >= MAX_CART_LINES) return [...cart];
  return [...cart, normalizeLine(line)];
}

export function setQuantity(cart: readonly CartLine[], catalogItemId: string, quantity: number): CartLine[] {
  if (quantity <= 0) return removeLine(cart, catalogItemId);
  return cart.map((l) => (l.catalogItemId === catalogItemId ? { ...l, quantity: clampQuantity(quantity) } : l));
}

export function setLineNote(cart: readonly CartLine[], catalogItemId: string, note: string): CartLine[] {
  return cart.map((l) => (l.catalogItemId === catalogItemId ? { ...l, note: note.slice(0, 1000) } : l));
}

export function setLineOrderField(cart: readonly CartLine[], catalogItemId: string, value: string): CartLine[] {
  return cart.map((l) => (l.catalogItemId === catalogItemId ? { ...l, orderFieldValue: value.slice(0, 300) } : l));
}

export function removeLine(cart: readonly CartLine[], catalogItemId: string): CartLine[] {
  return cart.filter((l) => l.catalogItemId !== catalogItemId);
}

/** Die Zahl am Korbsymbol: Positionen, nicht Stueckzahl. */
export function cartCount(cart: readonly CartLine[]): number {
  return cart.length;
}

export { STORAGE_KEY as CART_STORAGE_KEY, CHANGE_EVENT as CART_CHANGE_EVENT };

function clampQuantity(value: number): number {
  const n = Math.trunc(Number(value) || 1);
  return Math.min(Math.max(n, 1), MAX_LINE_QUANTITY);
}

function normalizeLine(line: CartLine): CartLine {
  return {
    catalogItemId: line.catalogItemId,
    name: String(line.name ?? '').slice(0, 200),
    manufacturer: String(line.manufacturer ?? '').slice(0, 120),
    quantity: clampQuantity(line.quantity),
    ...(line.note ? { note: String(line.note).slice(0, 1000) } : {}),
    ...(line.orderFieldValue ? { orderFieldValue: String(line.orderFieldValue).slice(0, 300) } : {}),
  };
}

function isCartLine(value: unknown): value is CartLine {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as CartLine).catalogItemId === 'string' &&
    (value as CartLine).catalogItemId !== ''
  );
}

function safeStorage(): Storage | undefined {
  try {
    return typeof window === 'undefined' ? undefined : window.localStorage;
  } catch {
    return undefined;
  }
}
